import React from 'react';
import { VariableSizeList, ListChildComponentProps } from 'react-window';
import Autocomplete, {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteRenderInputParams,
  createFilterOptions
} from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { Checkbox, ChipProps, ListSubheader, TextFieldProps, useMediaQuery } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import theme from '@/main/style/styleMaterialUIGlobal';
import KogInputText from '../inputText/inputText';
import { Spinner } from '../inputAutoComplete/styles';
import * as S from './styles';
import { useVisualIdentityReducer, VisualIdentityState } from '@/main/store/visualIdentity';
import { getTranslateByKey } from '@/main/services/utils';
const LISTBOX_PADDING = 8; // px

export interface IOption {
  key: string | number;
  value: string;
  group?: string;
}
const getOptionLabel = (option: IOption) => `${option.value}`;
const getOptionSelected = (option: IOption, anotherOption: IOption) => option.key === anotherOption.key;

const optionRenderer = (option: IOption, { selected }: any, disableSelectAll: boolean, allSelected: boolean, visualIdentityReducer: VisualIdentityState) => {
  const selectAllProps =
    option.key === 'select-all' // To control the state of 'select-all' checkbox
      ? { checked: allSelected }
      : {};

  if (option.key === 'select-all' && disableSelectAll) return null;
  return (
    <>
      <Checkbox
        color="primary"
        icon={<CheckBoxOutlineBlankIcon />}
        checkedIcon={<CheckBoxIcon
          style={{
            color: visualIdentityReducer.visualIdentity.colors.pantone2726C,
          }} />}
        checked={selected}
        {...selectAllProps}
      />
      <S.OptionTextLabel title={getOptionLabel(option)}>
        {getOptionLabel(option)}
      </S.OptionTextLabel>
    </>
  );
};

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data: any) {
  const ref = React.useRef<VariableSizeList>(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}
function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props;

  return React.cloneElement(data[index], {
    style: {
      ...style,
      padding: 0,
      top: (style.top as number) + LISTBOX_PADDING,
    },
  });
}
const ListboxComponent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(function ListboxComponent(props, ref) {
  const { children, ...other } = props;
  const itemData = React.Children.toArray(children);
  const smUp = useMediaQuery(theme.breakpoints.up('xl'), { noSsr: true });
  const itemCount = itemData.length;
  const itemSize = smUp ? 36 : 56;

  const getChildSize = (child: React.ReactNode) => {
    if (React.isValidElement(child) && child.type === ListSubheader) {
      return 48;
    }

    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={(index) => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});
export interface IKOGAutoCompleteCheckboxProps {
  options: IOption[];
  label: string;
  selectAllLabel?: string;
  noOptionsText?: string;
  onChangeCustom?: (value: IOption[], optionChanged: IOption | undefined, reason: AutocompleteChangeReason) => void;
  valueCustom?: IOption[];
  setValueCustom?: (value: IOption[]) => void;
  isLoading?: boolean;
  isFetching?: boolean;
  disableSelectAll?: boolean;
  errorMessageCustom?: string | null;
  customInputProps?: {} & TextFieldProps;
  classNameCustom?: string;
  disabled?: boolean;
  disabledOptions?: boolean;
  keysOptionsDisabled?: (string | number)[];
  showOnlyQuantityLabel?: boolean;
  id?: string;
  limitTags?: number;
  required?: boolean;
}

const KOGAutoCompleteCheckbox = React.forwardRef(({
  options = [],
  label,
  selectAllLabel = '',
  noOptionsText,
  errorMessageCustom = null,
  onChangeCustom = () => { },
  valueCustom,
  setValueCustom = () => { },
  customInputProps = {},
  isLoading = false,
  isFetching = false,
  disableSelectAll = false,
  classNameCustom = '',
  disabled = false,
  disabledOptions = false,
  keysOptionsDisabled = [],
  showOnlyQuantityLabel = false,
  id,
  required = false,
  ...props
}: IKOGAutoCompleteCheckboxProps, ref) => {
  const classes = S.useStyles();
  const [filteredOptions, setFilteredOptions] = React.useState<IOption[]>(options);
  const allSelected = options.length === valueCustom?.length;
  const getValuesKeysOptionsDisabled = () => (valueCustom?.filter(o => keysOptionsDisabled.includes(o.key)) ?? []);
  const isKeyOptionDisabled = (key: string | number) => keysOptionsDisabled.includes(key);

  const visualIdentityReducer = useVisualIdentityReducer();

  React.useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  const handleToggleOption = (value: IOption[]) => setValueCustom(value);
  const handleClearOptions = () => {
    const result = getValuesKeysOptionsDisabled();
    setValueCustom(result);
  }

  const getOptionDisabled = (option: IOption) => (disabledOptions && !valueCustom?.find((item) => item.key === option.key) || (keysOptionsDisabled as (string | number)[]).includes(option.key));

  const deduplicateByKey = (items: IOption[]): IOption[] => {
    const seen = new Set<string | number>();
    return items.filter(item => {
      if (seen.has(item.key)) return false;
      seen.add(item.key);
      return true;
    });
  };

  const handleChange = (_event: React.SyntheticEvent, value: IOption[], reason: AutocompleteChangeReason, details: AutocompleteChangeDetails<IOption> | undefined) => {
    if (isFetching) return null;

    const isOptionDisabled = details?.option && getOptionDisabled(details.option);
    if (isOptionDisabled) return null;

    if (reason === 'selectOption' || reason === 'removeOption') {
      if (details?.option?.key === 'select-all' && !disableSelectAll) {
        const filteredKeysSet = new Set(filteredOptions.map((opt: IOption) => opt.key));
        const selectedFilteredCount = valueCustom?.filter(item => filteredKeysSet.has(item.key)).length ?? 0;
        const isAllFilteredSelected = selectedFilteredCount === filteredOptions.length;

        let result: IOption[];
        if (isAllFilteredSelected) {
          result = valueCustom?.filter(item => !filteredKeysSet.has(item.key)) ?? [];
        } else {
          const filteredAvailableOptions = filteredOptions.filter(i => !isKeyOptionDisabled(i.key));
          const currentSelectedNotInFilter = valueCustom?.filter(selected => !filteredKeysSet.has(selected.key)) ?? [];
          result = deduplicateByKey([...currentSelectedNotInFilter, ...filteredAvailableOptions, ...getValuesKeysOptionsDisabled()]);
        }

        setValueCustom(result)
        setFilteredOptions(options)
        return onChangeCustom(result, details?.option, reason);
      }
      handleToggleOption && handleToggleOption(value);
      return onChangeCustom(value, details?.option, reason);

    } if (reason === 'clear') {
      handleClearOptions && handleClearOptions();
      const result = getValuesKeysOptionsDisabled();
      return onChangeCustom(result, details?.option, reason);
    }

    return onChangeCustom(value, details?.option, reason);
  };

  const inputRenderer = (params: AutocompleteRenderInputParams) => {
    return (
      <KogInputText
        {...customInputProps}
        {...params}
        ref={ref}
        error={!!errorMessageCustom}
        helperText={errorMessageCustom}
        label={label}
        variant="filled"
        id={customInputProps.id ? customInputProps.id : ''}
        required={required}
        inputProps={{
          ...params.inputProps
        }}
      />
    )
  }

  const renderTagsCustom = () => {
    if (!showOnlyQuantityLabel) return undefined;

    return () => (
      <span style={{ marginLeft: 8, fontSize: 14 }}>
        {valueCustomValid.length}
      </span>
    );
  };

  const filter = createFilterOptions();
  const valueCustomValid = Array.isArray(valueCustom) ? valueCustom : [];

  const allFilteredSelected = React.useMemo(() => {
    return options.length > 0 && valueCustomValid.length === options.length;
  }, [valueCustomValid, options.length]);

  const chipProps: ChipProps = {
    classes: {
      root: classes.chip
    }
  }

  const filterOptionsCallback = React.useCallback((optionsToFilter: IOption[], params: any) => {
    const filtered = filter(optionsToFilter, params) as IOption[];
    const effectiveSelectAllLabel = selectAllLabel || getTranslateByKey('information.selectAll');
    const filteredOptionsWithSelectAll = disableSelectAll ? filtered : [{ value: effectiveSelectAllLabel, key: 'select-all' }, ...filtered];
    return filteredOptionsWithSelectAll;
  }, [disableSelectAll, selectAllLabel]);

  return (
    <Autocomplete
      {...props}
      id={id}
      multiple
      disableListWrap
      size="small"
      disabled={disabled || isLoading || !options.length}
      className={`${classes.inputAutoCompleteCheckbox} ${classNameCustom}`}
      limitTags={1}
      options={options}
      {...(showOnlyQuantityLabel && { renderTags: renderTagsCustom() })}
      ListboxComponent={ListboxComponent as React.ComponentType<React.HTMLAttributes<HTMLElement>>}
      value={valueCustomValid}
      disableCloseOnSelect
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={getOptionSelected}
      getOptionDisabled={getOptionDisabled}
      ChipProps={chipProps}
      noOptionsText={noOptionsText}
      filterOptions={filterOptionsCallback}
      onChange={handleChange}
      popupIcon={isLoading ? <Spinner /> : <KeyboardArrowDown />}
      onInputChange={(event, value, reason) => {
        if (reason === 'input') {
          const filtered = filter(options, { inputValue: value, getOptionLabel: getOptionLabel as (option: unknown) => string }) as IOption[];
          setFilteredOptions(filtered);
        } else if (reason === 'clear') {
          setFilteredOptions(options);
        }
      }}
      renderOption={(renderProps, option, state) => (
        <li {...renderProps}>
          {optionRenderer(option, state, disableSelectAll, allFilteredSelected, visualIdentityReducer)}
        </li>
      )}
      renderInput={inputRenderer}
      onClose={() => setFilteredOptions(options)}
    />
  );
});

export default KOGAutoCompleteCheckbox;
